import React, { useEffect, useRef, useState } from 'react';
import { Box, Autocomplete, TextField, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import CustomButton from 'shared/custom-button/CustomButton';
import { colors } from 'app/providers/theme/colors-type';
import styles from 'features/cancer-protection/step-2/ui/Insurer.module.css';
import { IStepTwo } from 'entities/cancer-protection/step-2/types/StepTwo-inerface';
import { RoundedAutocomplete } from 'shared/basic-autocomplete/RoundedAutocomplete';
import { NestedKeyOf } from 'shared/utils/hooks/useFormRedux';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { FormFieldsStepOne } from 'entities/cancer-protection/step-1/selector';
import InfoIcon from '@mui/icons-material/Info';
import { CountriesData } from 'entities/cancer-protection/step-2/selsctor';
import { fetchDaDataAddres } from 'entities/cancer-protection/step-2/action/fetch-step-two';

interface InsurerProps {
    formState: IStepTwo;
    errors: Record<string, string>;
    handleNextStep: () => void;
    handleBack: () => void;
    openDatePicker: boolean;
    setOpenDatePicker: (value: boolean) => void;
    handleChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleDateChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    formatPhone: (phone: string) => string;
    handlePhoneChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleAutocompleteChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (value: string) => void;
    emailInput?: string;
    handleEmailInputChange?: (event: React.ChangeEvent<{}>, newValue: string) => void;
    handleDomainSelect?: (event: React.ChangeEvent<{}>, newValue: string | null) => void;
    filterDomains?: (options: string[], state: { inputValue: string }) => string[];
    emailDomains?: string[];
    handleCheckboxChange: (fieldName: keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    handleDocumentSeriesChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDocumentNumberChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDepartmentCodeChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleAddressChange: (fieldName: NestedKeyOf<IStepTwo> | keyof IStepTwo) => (value: string) => void;
    addressSuggestions: string[];
    isLoadingAddress: boolean;
}

const genderOptions = [
    { label: 'Мужской', value: 'Мужской' },
    { label: 'Женский', value: 'Женский' },
];


export const Insurer: React.FC<InsurerProps> = ({
    formState,
    errors,
    handleNextStep,
    handleBack,
    handleChange,
    handleBlur,
    handleDateChange,
    formatPhone,
    handlePhoneChange,
    handleAutocompleteChange,
    emailInput,
    handleEmailInputChange,
    handleDomainSelect,
    filterDomains,
    emailDomains,
    handleCheckboxChange,
    handleDocumentSeriesChange,
    handleDocumentNumberChange,
    handleDepartmentCodeChange,
    handleAddressChange,
    addressSuggestions,
    isLoadingAddress,
}) => {
    const [errorBirthDate, setErrorBirthDate] = useState(false);
    const { InsuredBirthDate } = useAppSelector(FormFieldsStepOne);

    useEffect(() => {
        if (formState.PolicyholderIsSame && formState.Insurer.BirthDate && InsuredBirthDate !== formState.Insurer.BirthDate) {
            setErrorBirthDate(true);
        } else {
            setErrorBirthDate(false);
        }
    }, [formState.Insurer.BirthDate, formState.PolicyholderIsSame]);

    const { Countries, isLoadingCountries } = useAppSelector(CountriesData);

    const selectedCountry = Countries?.find(country => country.ID === formState.Insurer.CountryID) || null;
    const dispatch = useAppDispatch();

    const { AgentSalesDataID } = useAppSelector(FormFieldsStepOne);

    const [addressInput, setAddressInput] = useState(formState.Insurer.Address);
    const [openSuggestions, setOpenSuggestions] = useState(false);

    const lastRequestedBucket = useRef(0);
    const addressFocused = useRef(false);

    useEffect(() => {
        handleAutocompleteChange('Insurer.CountryID')('7434be15-0c71-11eb-80d7-00155d65b075');
    }, [])

    useEffect(() => {
        setAddressInput(formState.Insurer.Address);
    }, [formState.Insurer.Address]);

    const handleAddressInputChange = (
        _event: React.SyntheticEvent,
        newValue: string,
        reason: string,
    ) => {
        setAddressInput(newValue);

        if (reason === 'clear') {
            lastRequestedBucket.current = 0;
            setOpenSuggestions(false);
            handleAddressChange('Insurer.Address')('');
            return;
        }
        const length = newValue.length;
        if (length < 3) {
            lastRequestedBucket.current = 0;
            setOpenSuggestions(false);
            return;
        }
        const bucket = Math.floor(length / 3);
        if (bucket < lastRequestedBucket.current) {
            lastRequestedBucket.current = bucket;
            return;
        }

        if (
            length % 3 === 0 &&
            bucket !== lastRequestedBucket.current
        ) {
            lastRequestedBucket.current = bucket;

            dispatch(
                fetchDaDataAddres({
                    Text: newValue,
                    AgentSalesDataID,
                }),
            );
        }
    };

    useEffect(() => {
        if (
            addressFocused.current &&
            addressInput.length >= 3 &&
            !isLoadingAddress
        ) {
            setOpenSuggestions(true);
        }
    }, [addressSuggestions, isLoadingAddress, addressInput.length]);

    const handleAddressSelect = (
        _event: React.SyntheticEvent,
        newValue: string | null,
    ) => {
        if (newValue === null) {
            return;
        }
        setAddressInput(newValue);
        handleAddressChange('Insurer.Address')(newValue);
    };

    const handleAddressFocus = () => {
        addressFocused.current = true;
        if (addressInput.length >= 3 && !isLoadingAddress) {
            setOpenSuggestions(true);
        }
    };

    const handleAddressBlur = () => {
        addressFocused.current = false;
        setOpenSuggestions(false);
        handleAddressChange('Insurer.Address')(addressInput);
    };

    const isRussia = Countries?.find(country => country.ID === formState.Insurer.CountryID)?.Name === 'РОССИЯ';

    return (
        <Box>
            <Box className={styles.formBox}>
                <Box className={styles.titleBox}>
                    <h4>Страхователь</h4>
                    <p>Тот, кто подписывает договор со страховой компанией</p>
                </Box>
                <Box className={styles.formFields}>
                    <Box className={styles.twoLine}>
                        <TextField
                            name="Insurer.LastName"
                            label="Фамилия"
                            value={formState.Insurer.LastName}
                            onChange={handleChange('Insurer.LastName')}
                            onBlur={(e) => {
                                handleBlur('Insurer.LastName')(e);
                            }}
                            error={!!errors['Insurer.LastName']}
                            helperText={errors['Insurer.LastName']}
                        />
                        <TextField
                            name="Insurer.FirstName"
                            label="Имя"
                            value={formState.Insurer.FirstName}
                            onChange={handleChange('Insurer.FirstName')}
                            onBlur={(e) => {
                                handleBlur('Insurer.FirstName')(e);
                            }}
                            error={!!errors['Insurer.FirstName']}
                            helperText={errors['Insurer.FirstName']}
                        />
                    </Box>
                    <Box className={styles.twoLine}>
                        <TextField
                            name="Insurer.MiddleName"
                            label="Отчество"
                            value={formState.Insurer.MiddleName}
                            onChange={handleChange('Insurer.MiddleName')}
                            onBlur={(e) => {
                                handleBlur('Insurer.MiddleName')(e);
                            }}
                            error={!!errors['Insurer.MiddleName']}
                            helperText={errors['Insurer.MiddleName']}
                        />
                        <TextField
                            name="Insurer.BirthDate"
                            label="Дата рождения"
                            value={formState.Insurer.BirthDate}
                            placeholder="дд.мм.гггг"
                            onChange={handleDateChange('Insurer.BirthDate')}
                            onBlur={(e) => {
                                handleBlur('Insurer.BirthDate')(e);
                            }}
                            error={!!errors['Insurer.BirthDate']}
                            helperText={errors['Insurer.BirthDate']}
                        />
                    </Box>
                    <Box className={styles.twoLine}>
                        <RoundedAutocomplete
                            name="Insurer.Gender"
                            label="Пол"
                            value={formState.Insurer.Gender}
                            onChange={(value) => {
                                handleAutocompleteChange('Insurer.Gender')(value);
                            }}
                            onBlur={() => {
                                handleBlur('Insurer.Gender')({
                                    target: { value: formState.Insurer.Gender },
                                    currentTarget: { value: formState.Insurer.Gender } as HTMLInputElement,
                                } as React.FocusEvent<HTMLInputElement>);
                            }}
                            options={genderOptions}
                            error={!!errors['Insurer.Gender']}
                            helperText={errors['Insurer.Gender']}
                        />
                        <Autocomplete
                            freeSolo
                            options={emailDomains || []}
                            value={null}
                            inputValue={emailInput || ''}
                            onInputChange={handleEmailInputChange || (() => { })}
                            onChange={handleDomainSelect || (() => { })}
                            filterOptions={filterDomains || (() => [])}
                            getOptionLabel={(option) => option}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="Insurer.Email"
                                    label="Почта"
                                    variant="outlined"
                                    className={styles.fullWidth}
                                    error={!!errors['Insurer.Email']}
                                    helperText={errors['Insurer.Email']}
                                />
                            )}
                        />
                    </Box>
                    <Box className={styles.twoLine}>
                        <TextField
                            name="Insurer.Phone"
                            label="Телефон"
                            variant="outlined"
                            className={styles.fullWidth}
                            value={formatPhone(formState.Insurer.Phone)}
                            onChange={handlePhoneChange('Insurer.Phone')}
                            onBlur={handleBlur('Insurer.Phone')}
                            error={!!errors['Insurer.Phone']}
                            helperText={errors['Insurer.Phone']}
                        />
                    </Box>
                    <Box className={styles.titleOterBox}>
                        <h4>Гражданство</h4>
                    </Box>
                    <Box className={styles.formFields}>
                        <Autocomplete
                            options={Countries || []}
                            getOptionLabel={(option) => option.Name}
                            isOptionEqualToValue={(option, value) => option.ID === value.ID}
                            value={selectedCountry}
                            onChange={(_event, newValue) => {
                                handleAutocompleteChange('Insurer.CountryID')(newValue ? newValue.ID : '');
                            }}
                            onBlur={() => {
                                handleBlur('Insurer.CountryID')({
                                    target: { value: formState.Insurer.CountryID },
                                    currentTarget: { value: formState.Insurer.CountryID } as HTMLInputElement,
                                } as React.FocusEvent<HTMLInputElement>);
                            }}
                            loading={isLoadingCountries}
                            loadingText="Загрузка..."
                            noOptionsText="Страна не найдена"
                            disabled={true}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Страна"
                                    error={!!errors['Insurer.CountryID']}
                                    helperText={errors['Insurer.CountryID']}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoadingCountries ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            filterOptions={(options, state) => {
                                const inputValue = state.inputValue.trim().toLowerCase();
                                if (inputValue === '') {
                                    return options;
                                }
                                return options.filter(option =>
                                    option.Name.toLowerCase().includes(inputValue)
                                );
                            }}
                            popupIcon={null}
                            fullWidth
                        />
                    </Box>
                    {formState.Insurer.CountryID && (
                        <>
                            <Box className={styles.titleOterBox}>
                                <h4>Паспортные данные</h4>
                            </Box>
                            {formState.Insurer.CountryID && (
                                <Box className={styles.formFields}>
                                    <Box className={styles.twoLine}>
                                        <TextField
                                            name="Insurer.DocumentSeries"
                                            label="Серия"
                                            value={formState.Insurer.DocumentSeries}
                                            onChange={handleDocumentSeriesChange('Insurer.DocumentSeries')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentSeries')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentSeries']}
                                            helperText={errors['Insurer.DocumentSeries']}
                                        />
                                        <TextField
                                            name="Insurer.DocumentNumber"
                                            label="Номер"
                                            value={formState.Insurer.DocumentNumber}
                                            onChange={handleDocumentNumberChange('Insurer.DocumentNumber')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentNumber')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentNumber']}
                                            helperText={errors['Insurer.DocumentNumber']}
                                        />
                                    </Box>
                                    <Box className={styles.twoLine}>
                                        <TextField
                                            name="Insurer.DocumentDate"
                                            label="Дата выдачи"
                                            value={formState.Insurer.DocumentDate}
                                            placeholder="дд.мм.гггг"
                                            onChange={handleDateChange('Insurer.DocumentDate')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentDate')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentDate']}
                                            helperText={errors['Insurer.DocumentDate']}
                                        />
                                        <TextField
                                            name="Insurer.DepartmentCode"
                                            label="Код подразделения"
                                            value={formState.Insurer.DepartmentCode}
                                            onChange={handleDepartmentCodeChange('Insurer.DepartmentCode')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DepartmentCode')(e);
                                            }}
                                            error={!!errors['Insurer.DepartmentCode']}
                                            helperText={errors['Insurer.DepartmentCode']}
                                            placeholder="000-000"
                                        />
                                    </Box>
                                    <Box className={styles.oneLine}>
                                        <TextField
                                            name="Insurer.DocumentDepartment"
                                            label="Кем выдан"
                                            value={formState.Insurer.DocumentDepartment}
                                            onChange={handleChange('Insurer.DocumentDepartment')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentDepartment')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentDepartment']}
                                            helperText={errors['Insurer.DocumentDepartment']}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {/* {formState.Insurer.CountryID && isRussia ? (
                                <Box className={styles.formFields}>
                                    <Box className={styles.twoLine}>
                                        <TextField
                                            name="Insurer.DocumentSeries"
                                            label="Серия"
                                            value={formState.Insurer.DocumentSeries}
                                            onChange={handleDocumentSeriesChange('Insurer.DocumentSeries')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentSeries')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentSeries']}
                                            helperText={errors['Insurer.DocumentSeries']}
                                        />
                                        <TextField
                                            name="Insurer.DocumentNumber"
                                            label="Номер"
                                            value={formState.Insurer.DocumentNumber}
                                            onChange={handleDocumentNumberChange('Insurer.DocumentNumber')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentNumber')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentNumber']}
                                            helperText={errors['Insurer.DocumentNumber']}
                                        />
                                    </Box>
                                    <Box className={styles.twoLine}>
                                        <TextField
                                            name="Insurer.DocumentDate"
                                            label="Дата выдачи"
                                            value={formState.Insurer.DocumentDate}
                                            placeholder="дд.мм.гггг"
                                            onChange={handleDateChange('Insurer.DocumentDate')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentDate')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentDate']}
                                            helperText={errors['Insurer.DocumentDate']}
                                        />
                                        <TextField
                                            name="Insurer.DepartmentCode"
                                            label="Код подразделения"
                                            value={formState.Insurer.DepartmentCode}
                                            onChange={handleDepartmentCodeChange('Insurer.DepartmentCode')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DepartmentCode')(e);
                                            }}
                                            error={!!errors['Insurer.DepartmentCode']}
                                            helperText={errors['Insurer.DepartmentCode']}
                                            placeholder="000-000"
                                        />
                                    </Box>
                                    <Box className={styles.oneLine}>
                                        <TextField
                                            name="Insurer.DocumentDepartment"
                                            label="Кем выдан"
                                            value={formState.Insurer.DocumentDepartment}
                                            onChange={handleChange('Insurer.DocumentDepartment')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentDepartment')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentDepartment']}
                                            helperText={errors['Insurer.DocumentDepartment']}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Box className={styles.formFields}>
                                    <Box className={styles.twoLine}>
                                        <TextField
                                            name="Insurer.DocumentSeries"
                                            label="Серия"
                                            value={formState.Insurer.DocumentSeries}
                                            onChange={handleChange('Insurer.DocumentSeries')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentSeries')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentSeries']}
                                            helperText={errors['Insurer.DocumentSeries']}
                                        />
                                        <TextField
                                            name="Insurer.DocumentNumber"
                                            label="Номер"
                                            value={formState.Insurer.DocumentNumber}
                                            onChange={handleChange('Insurer.DocumentNumber')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentNumber')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentNumber']}
                                            helperText={errors['Insurer.DocumentNumber']}
                                        />
                                    </Box>
                                    <Box className={styles.twoLine}>
                                        <TextField
                                            name="Insurer.DocumentDate"
                                            label="Дата выдачи"
                                            value={formState.Insurer.DocumentDate}
                                            placeholder="дд.мм.гггг"
                                            onChange={handleDateChange('Insurer.DocumentDate')}
                                            onBlur={(e) => {
                                                handleBlur('Insurer.DocumentDate')(e);
                                            }}
                                            error={!!errors['Insurer.DocumentDate']}
                                            helperText={errors['Insurer.DocumentDate']}
                                        />
                                    </Box>
                                </Box>
                            )} */}

                            <Box className={styles.titleOterBox}>
                                <h4>Адрес постоянной регистрации</h4>
                            </Box>
                            <Box className={styles.formFields}>
                                <Box className={styles.oneLine}>
                                    <Autocomplete
                                        freeSolo
                                        fullWidth
                                        value={null}
                                        inputValue={addressInput}
                                        options={addressSuggestions}
                                        open={openSuggestions}
                                        onInputChange={handleAddressInputChange}
                                        onChange={handleAddressSelect}
                                        onFocus={handleAddressFocus}
                                        onBlur={handleAddressBlur}
                                        loading={isLoadingAddress}
                                        loadingText="Загрузка..."
                                        noOptionsText="Адреса не найдены"
                                        filterOptions={(options) => options}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name="Insurer.Address"
                                                label="Адрес"
                                                error={!!errors['Insurer.Address']}
                                                helperText={errors['Insurer.Address']}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {isLoadingAddress && (
                                                                <CircularProgress
                                                                    color="inherit"
                                                                    size={20}
                                                                />
                                                            )}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        </>
                    )}
                    {errorBirthDate && (
                        <Box className={styles.errorBox}>
                            <span className={styles.iconRed}><InfoIcon /></span>
                            <Box className={styles.textErrorBox}>
                                <p>Дата рождения не соответсвует дате введеной на этапе "Расчет".</p>
                                <p>Проверьте данные страхователя или вернитесь на шаг "Расчет" и введите корректную дату.</p>
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box className={styles.checkboxField}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="PolicyholderIsSame"
                                checked={Boolean(formState.PolicyholderIsSame)}
                                onChange={handleCheckboxChange('PolicyholderIsSame')}
                                sx={{
                                    color: 'grey.500',
                                    '&.Mui-checked': {
                                        color: colors.primary,
                                    },
                                }}
                            />
                        }
                        label="Буду являться застрахованным"
                    />
                </Box>
            </Box>
            <Box className={styles.btnBoxNext}>
                {!errorBirthDate && (
                    <CustomButton
                        title="Далее"
                        handleClick={handleNextStep}
                        textColor="white"
                        backgroundColor={colors.primary}
                        width="150px"
                        gap="10px"
                        borderRadius='4px'
                        padding='3px'
                    />
                )}

            </Box>
        </Box>
    );
};


